import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Heart,
  Wand2,
  Share2,
  Loader2,
  Users,
  ExternalLink,
  Sparkles,
  Star,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import SiteFooter from '../components/SiteFooter';
import AuthDialog from '../components/AuthDialog';
import { Toaster } from '../components/ui/toaster';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADIENT_MAP = {
  'cyan-magenta': 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)',
  'silver-slate': 'linear-gradient(135deg, #e2e8f0 0%, #64748b 100%)',
  'emerald-lime': 'linear-gradient(135deg, #10b981 0%, #a3e635 100%)',
  'amber-crimson': 'linear-gradient(135deg, #f59e0b 0%, #dc2626 100%)',
  'sky-lavender': 'linear-gradient(135deg, #38bdf8 0%, #c084fc 100%)',
  'violet-gold': 'linear-gradient(135deg, #8b5cf6 0%, #fbbf24 100%)',
};
const gradientFor = (k) => GRADIENT_MAP[k] || GRADIENT_MAP['cyan-magenta'];

export default function AuthorPage() {
  const { handle: paramHandle } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  // useParams may miss the handle if the route matched via a catch-all — fall back to pathname
  const pathHandle = location.pathname.replace(/^\//, '').replace(/^@/, '').split('/')[0];
  const cleanHandle = (paramHandle || pathHandle || '').replace(/^@/, '');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/authors/${encodeURIComponent(cleanHandle)}`);
        if (r.ok) setData(await r.json());
        else setData(null);
      } catch {
        setData(null);
      }
      setLoading(false);
    })();
  }, [cleanHandle]);

  const activateSoul = (s) => {
    localStorage.setItem('atw_soul', s.system_prompt);
    localStorage.setItem('atw_soul_meta', JSON.stringify({
      name: s.name, emoji: s.emoji, slug: s.slug,
    }));
    fetch(`${API}/souls/${s.slug}/use`, { method: 'POST' }).catch(() => {});
    toast({
      title: `${s.emoji || '✨'} ${s.name} activated`,
      description: 'The AI now speaks in this personality — head to the builder.',
    });
    setTimeout(() => navigate('/build'), 700);
  };

  const shareAuthor = () => {
    const url = `${window.location.origin}/@${cleanHandle}`;
    try {
      navigator.clipboard.writeText(url);
      toast({ title: 'Link copied', description: url });
    } catch { /* noop */ }
  };

  const shareSoul = (s) => {
    const url = `${BACKEND_URL}/api/share/soul/${s.slug}`;
    try {
      navigator.clipboard.writeText(url);
      toast({ title: 'Share link copied', description: 'Card previews on Twitter/LinkedIn.' });
    } catch { /* noop */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] text-white">
        <Navbar onGetStarted={() => setAuthOpen(true)} />
        <div className="pt-40 flex items-center justify-center text-white/60">
          <Loader2 size={22} className="animate-spin mr-2" /> Loading creator page…
        </div>
        <Toaster />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#030303] text-white">
        <Navbar onGetStarted={() => setAuthOpen(true)} />
        <div className="pt-40 max-w-xl mx-auto px-6 text-center">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50">Not found</div>
          <h1 className="mt-3 text-4xl font-display font-black text-white">Nobody at that handle yet.</h1>
          <p className="mt-3 text-white/60">We couldn't find a creator at <span className="font-mono text-white">/@{cleanHandle}</span>.</p>
          <div className="mt-6">
            <Link to="/souls" className="rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[14px] font-medium px-5 py-2.5 inline-flex items-center gap-1.5">
              <ArrowLeft size={14} /> Browse SOUL Library
            </Link>
          </div>
        </div>
        <Toaster />
      </div>
    );
  }

  const { author, souls, stats } = data;

  return (
    <div className="relative bg-[#030303] text-white min-h-screen overflow-hidden">
      <Navbar onGetStarted={() => setAuthOpen(true)} />

      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full blur-3xl opacity-20"
        style={{ background: 'radial-gradient(circle, #00F0FF33 0%, transparent 70%)' }}
      />

      <section className="relative pt-32 pb-8 px-6 lg:px-10 max-w-5xl mx-auto">
        <Link
          to="/souls"
          data-testid="author-back-link"
          className="inline-flex items-center gap-1.5 text-[13px] text-white/60 hover:text-white transition"
        >
          <ArrowLeft size={13} /> SOUL Library
        </Link>

        {/* Author card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mt-6 rounded-3xl border border-white/10 bg-black/40 backdrop-blur-xl overflow-hidden"
          data-testid="author-hero"
        >
          <div
            className="h-32 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)' }}
          >
            <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{
              background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35), transparent 45%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.4), transparent 45%)',
            }} />
          </div>

          <div className="px-6 md:px-8 pb-8 -mt-14 relative flex flex-col md:flex-row md:items-end gap-6">
            <div
              className="h-24 w-24 md:h-28 md:w-28 rounded-2xl bg-black/70 backdrop-blur-md border border-white/20 flex items-center justify-center text-4xl md:text-5xl font-black shrink-0"
              data-testid="author-avatar"
              style={{ color: '#00F0FF' }}
            >
              @{(author.handle || 'x').slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0 md:pb-2">
              <div className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50">Creator</div>
              <h1 className="mt-1 text-3xl md:text-4xl font-display font-black text-white">
                {author.name || 'Anonymous'}
              </h1>
              <div className="mt-1 text-[14px] font-mono text-white/50">/@{author.handle}</div>
              {author.bio && (
                <p className="mt-3 text-[14.5px] text-white/75 max-w-2xl leading-relaxed">
                  {author.bio}
                </p>
              )}
              {author.link && (
                <a
                  href={author.link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="author-link"
                  className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-cyan-300 hover:text-cyan-200 transition"
                >
                  {author.link.replace(/^https?:\/\//, '')} <ExternalLink size={11} />
                </a>
              )}
            </div>
            <button
              onClick={shareAuthor}
              data-testid="author-share"
              className="shrink-0 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[13px] font-medium px-4 py-2.5 inline-flex items-center gap-1.5 transition"
            >
              <Share2 size={13} /> Share page
            </button>
          </div>
        </motion.div>

        {/* Stats bar */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          <StatChip label="Personalities" value={stats.soul_count} icon={Sparkles} tone="cyan" testid="author-stat-count" />
          <StatChip label="Total likes" value={stats.total_likes} icon={Heart} tone="magenta" testid="author-stat-likes" />
          <StatChip label="Total uses" value={stats.total_uses} icon={Wand2} tone="amber" testid="author-stat-uses" />
        </div>

        {/* Souls grid */}
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Users size={14} className="text-white/50" />
            <span className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50">
              Published personalities
            </span>
          </div>

          {souls.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center text-white/50">
              This creator hasn't published a SOUL yet.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="author-souls-grid">
              {souls.map((s) => (
                <motion.div
                  key={s.slug}
                  whileHover={{ y: -3 }}
                  transition={{ duration: 0.15 }}
                  data-testid={`author-soul-${s.slug}`}
                  className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl overflow-hidden"
                >
                  <div
                    className="h-20 relative overflow-hidden"
                    style={{ background: gradientFor(s.gradient) }}
                  >
                    <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{
                      background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4), transparent 45%)',
                    }} />
                    <div className="absolute bottom-2 left-3 flex items-center gap-2">
                      <div className="h-10 w-10 rounded-lg bg-black/40 backdrop-blur-md border border-white/20 flex items-center justify-center text-xl">
                        {s.emoji || (s.name || 'S').slice(0, 1)}
                      </div>
                      <div>
                        <Link
                          to={`/souls/${s.slug}`}
                          className="text-[13.5px] font-black text-white leading-tight hover:text-cyan-200 transition"
                        >
                          {s.name}
                        </Link>
                      </div>
                    </div>
                    {s.featured && (
                      <div className="absolute top-2 right-2 rounded-full bg-black/50 backdrop-blur-md border border-white/20 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-white flex items-center gap-1">
                        <Star size={9} className="text-amber-300" fill="currentColor" /> Featured
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-[13px] text-white/70 leading-relaxed line-clamp-2 min-h-[38px]">
                      {s.tagline}
                    </p>
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-white/50">
                      <span className="inline-flex items-center gap-1"><Heart size={11} /> {s.likes}</span>
                      <span className="inline-flex items-center gap-1"><Wand2 size={11} /> {s.uses} uses</span>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        onClick={() => activateSoul(s)}
                        data-testid={`author-soul-use-${s.slug}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg text-black text-[12.5px] font-semibold py-2 hover:scale-[1.02] transition"
                        style={{ background: gradientFor(s.gradient) }}
                      >
                        Use SOUL
                      </button>
                      <button
                        onClick={() => shareSoul(s)}
                        data-testid={`author-soul-share-${s.slug}`}
                        className="h-8 w-8 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/70 hover:text-white flex items-center justify-center transition"
                        title="Copy share link"
                      >
                        <Share2 size={11} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="h-24" />
      <SiteFooter />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      <Toaster />
    </div>
  );
}

function StatChip({ label, value, icon: Icon, tone, testid }) {
  const toneMap = {
    cyan: { bg: 'rgba(0,240,255,.08)', border: 'rgba(0,240,255,.25)', text: '#7dd3fc' },
    magenta: { bg: 'rgba(255,0,127,.08)', border: 'rgba(255,0,127,.25)', text: '#f472b6' },
    amber: { bg: 'rgba(251,191,36,.08)', border: 'rgba(251,191,36,.25)', text: '#fbbf24' },
  }[tone];
  return (
    <div
      data-testid={testid}
      className="rounded-2xl p-4"
      style={{ background: toneMap.bg, border: `1px solid ${toneMap.border}` }}
    >
      <div className="flex items-center gap-2" style={{ color: toneMap.text }}>
        <Icon size={13} />
        <span className="text-[10.5px] font-mono uppercase tracking-[0.25em]">{label}</span>
      </div>
      <div className="mt-2 text-3xl font-display font-black text-white">{value}</div>
    </div>
  );
}
